import type { StoryElement } from '../types';
import type { SavedSearchRunStatus } from '../repositories/savedSearchRepository';

/**
 * Client-side narrowing of a 72h search result set down to the 24h view.
 *
 * The window is a hard product rule owned server-side by
 * `backend/app/services/search_window_policy.py`; nothing here widens a window
 * or adds a fallback. It only re-applies the *narrower* of two windows to
 * results the server already returned, so that flipping 72h -> 24h is instant
 * and flipping back to 72h repopulates from memory instead of re-running.
 *
 * The window and the instant it was anchored on come from the run's own status
 * payload (`window_hours`, `created_at`), never from composer state. A previous
 * attempt (reverted in cd6d64fa) tracked them in the composer's own useState,
 * which any remount or bookmarked-run restore silently reset to "no run yet" —
 * and a null run window makes narrowing a no-op, so the 24h view displayed the
 * whole 72h set and both widths showed identical results. Deriving it from the
 * server means the results on screen and the width claimed for them cannot come
 * apart.
 */
export interface SearchRunWindow {
  /** The lookback the run's candidate retrieval actually used. */
  hours: number;
  /** The run's start instant, the anchor its cutoff was measured back from. */
  startMs: number;
}

/**
 * The window a status payload describes, or null if it does not describe one.
 *
 * Both fields are required: a window with no anchor cannot be re-applied, and
 * claiming a width without one would narrow against the wrong instant.
 */
export function runWindowFromStatus(
  status: Pick<SavedSearchRunStatus, 'window_hours' | 'created_at'>,
): SearchRunWindow | null {
  const hours = status.window_hours;
  if (typeof hours !== 'number' || !Number.isFinite(hours) || hours <= 0) return null;
  const startMs = status.created_at ? Date.parse(status.created_at) : NaN;
  if (Number.isNaN(startMs)) return null;
  return { hours, startMs };
}

/**
 * The *capping* effective time of a row: the earlier of published_at and
 * created_at.
 *
 * This must match the rule the server enforces the window with — see
 * `app.queries.effective_publication`, which spells out why recency windows
 * use earliest rather than prefer-published: a future-dated published_at must
 * not hold an article inside an age window forever.
 */
export function cappingEffectiveTimeMs(story: StoryElement): number | null {
  const candidates = [story.published_at, story.created_at]
    .map((value) => (value ? Date.parse(value) : NaN))
    .filter((value) => !Number.isNaN(value));
  return candidates.length ? Math.min(...candidates) : null;
}

/**
 * True when `story` falls inside the `windowHours` lookback ending at
 * `runStartMs`.
 *
 * A row carrying neither timestamp cannot be shown to be outside the window,
 * so it is kept: the 24h view is a subset of what the server already admitted,
 * and dropping an unknown-age row would hide a result the server vouched for.
 */
export function withinSearchWindow(
  story: StoryElement,
  runStartMs: number,
  windowHours: number,
): boolean {
  const effective = cappingEffectiveTimeMs(story);
  if (effective === null) return true;
  return effective >= runStartMs - windowHours * 3600_000;
}

/**
 * The rows to display, given the run that produced them and the width selected.
 *
 * Narrowing (a 72h run displayed at 24h) filters; every other combination is
 * the identity, because widening is a server question — the caller runs a
 * fresh search for that (see `searchWindowAction`).
 */
export function narrowToSelectedWindow(
  stories: StoryElement[],
  runWindow: SearchRunWindow | null,
  selectedWindowHours: number,
): StoryElement[] {
  if (!runWindow) return stories;
  if (selectedWindowHours >= runWindow.hours) return stories;
  return stories.filter(
    (story) => withinSearchWindow(story, runWindow.startMs, selectedWindowHours),
  );
}

/**
 * What selecting `hours` should do, given the run whose results are on screen.
 *
 * - `'filter'`: the run already looked at least this far back, so the selected
 *   view is a subset of what is in hand. Never re-runs — that is what keeps
 *   72h -> 24h -> 72h instant in both directions.
 * - `'search'`: the selection reaches past what the run retrieved, so the
 *   candidates simply are not in hand. Only a fresh run can produce them.
 * - `'none'`: nothing has run yet; the control is just choosing the width the
 *   pending search will use.
 */
export function searchWindowAction(
  runWindow: SearchRunWindow | null,
  hours: number,
): 'filter' | 'search' | 'none' {
  if (!runWindow) return 'none';
  return hours <= runWindow.hours ? 'filter' : 'search';
}
